import {createFileRoute, useNavigate} from "@tanstack/react-router"
import {PageBody, PageHeader, PageShell} from "../../../../routing/shell.tsx";
import {Stack} from "@mui/joy";
import {TopBar} from "../../../../components/TopBar.tsx";
import {Comparator} from "../../../../util/arutil.tsx";
import {cmpScenesNewestFirst} from "../../../../timeline/timeline.tsx";
import {useCallback, useMemo} from "react";
import {SuspenseDialog} from "../../../../components/SuspenseDialog.tsx";
import {TileSpread} from "../../../../components/Tile.tsx";
import {InspectorBundleTile} from "../../../../inspector/InspectorBundleTile.tsx";
import {IconToggleSwitch, ToggleBar} from "../../../../components/ToggleBar.tsx";
import {useBomb} from "../../../../bomb/bomb.tsx";
import {InspectorFacet, inspectorFacetIcons, validateInspectionSettings} from "../../../../inspector/navi.tsx";
import {TieredOrActive} from "../../../../dispatch/tieredOrActive.tsx";
import {SortOrder} from "../../../../rpc/webrpc-client.gen.ts";
import {tieredActiveIcons} from "../../../../fa.tsx";
import {sortOrderIcons} from "../../../../tq/tqutil.tsx";
import {VenuedBundle} from "../../../../venue/overview.tsx";

import {useInspectorBundles} from "../../../../inspector/inspector.tsx";
import {useSystemConfig} from "../../../../rpc/sysconf.tsx";

function InspectBundleSpread({venueId}: { venueId: string }) {
    const ixbsTqrv = useInspectorBundles({venueId});
    const cmp: Comparator<VenuedBundle> = (a, b) => {
        return cmpScenesNewestFirst(a.lastScene, b.lastScene);
    };
    const sortedIxbs = useMemo(() => {
        const sorted = (ixbsTqrv.data ?? []).toSorted(cmp);
        return sorted;
    }, [ixbsTqrv.data]);

    if (ixbsTqrv.isPending) {
        return <SuspenseDialog message="fetching inspectable bundles"/>;
    }

    return <TileSpread foods={sortedIxbs} estimatedHeight={240}
                       Component={({food: ixb}) =>
                           <InspectorBundleTile ixb={ixb} key={ixb.bundle.id}/>
                       }>
    </TileSpread>

}

function InspectBundlePage() {
    const pathParams = Route.useParams();
    const venueId = pathParams.id;
    const searchParams = Route.useSearch();
    const sysConfigTqrv = useSystemConfig();
    const showFacets = sysConfigTqrv.data?.showInspectorFacets;
    const navigate = useNavigate();
    const bomb = useBomb();
    const ixbTqrv = useInspectorBundles({venueId});

    const setOrder = useCallback((next: TieredOrActive) => {
        navigate({to: Route.to, params: pathParams, search: {...searchParams, order: next}}).catch(bomb);
    }, [bomb, navigate, pathParams, searchParams]);

    const setFacet = useCallback((facet: InspectorFacet) => {
        navigate({
            to: `../${facet}`,
            params: {id: venueId},
            search: searchParams,
        }).catch(bomb);
    }, [bomb, navigate, searchParams, venueId]);

    const setSortOrder = useCallback((next: SortOrder) => {
        navigate({to: Route.to, params: pathParams, search: {...searchParams, sortOrder: next}}).catch(bomb);
    }, [bomb, navigate, pathParams, searchParams]);

    return <PageShell>
        <PageHeader>
            <Stack direction="column">
                <TopBar/>

                <ToggleBar>
                    {showFacets
                        ? <>
                            <IconToggleSwitch dispatchValue={setOrder} icons={tieredActiveIcons}
                                              value={searchParams.order}
                                              disabled={!((ixbTqrv.data?.length ?? 0) > 1)}/>
                            <IconToggleSwitch dispatchValue={setFacet} icons={inspectorFacetIcons} value="bundle"/>
                        </>
                        : null}
                    <IconToggleSwitch dispatchValue={setSortOrder} icons={sortOrderIcons} value={searchParams.sortOrder}
                                      disabled={!((ixbTqrv.data?.length ?? 0) > 1)}/>
                </ToggleBar>
            </Stack>
        </PageHeader>
        <PageBody>
            <InspectBundleSpread venueId={venueId}/>
        </PageBody>
    </PageShell>;
}


export const Route = createFileRoute("/_auth/venue/$id/inspect/bundle")({
    component: InspectBundlePage,
    validateSearch: validateInspectionSettings,
});

